/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import { data, type ObservableValue } from '@amazon/vinyl-observable'
import { createDisposer, type Unsubscribe } from '@amazon/vinyl-util'
import { globalData, type Player } from '@amzn/bf-playback'

/**
 * Opaque handle for a media source that can hold exclusive playback. The main
 * transport uses {@link TRANSPORT_MEDIA_SOURCE}; every other media surface (a
 * lazy YouTube embed today, an inline player tomorrow) mints its own unique id.
 */
export type MediaSourceId = string

/** The single id the main transport claims. */
export const TRANSPORT_MEDIA_SOURCE = 'transport'

const activeSource = globalData(() => data<MediaSourceId | null>(null))

/**
 * The media source currently holding playback, or null before anything has
 * played. At most one source is ever active — claiming a new one is what makes
 * the others yield.
 */
export const activeMediaSource$: ObservableValue<MediaSourceId | null> = activeSource

/**
 * Take exclusive playback for `sourceId`: every OTHER source registered via
 * {@link onMediaYield} runs its yield handler, so at most one media source
 * plays at a time. Re-claiming the already-active source is a no-op.
 */
export function claimMediaPlayback(sourceId: MediaSourceId): void {
    activeSource.value = sourceId
}

/**
 * Run `onYield` whenever a DIFFERENT source claims playback — the cue for this
 * source to pause/stop. Returns an unsubscribe. The owner at subscribe time is
 * deliberately not replayed: a source yields only to a subsequent claim, never
 * to the state it mounted into (a freshly-mounted source has nothing playing to
 * interrupt).
 */
export function onMediaYield(sourceId: MediaSourceId, onYield: () => void): Unsubscribe {
    let replayed = false
    return activeSource.onData((active) => {
        if (!replayed) {
            replayed = true
            return
        }
        if (active !== sourceId) onYield()
    })
}

/**
 * Makes the transport's player one media source among several (e.g. lazy YouTube embeds): it
 * claims playback whenever the engine starts producing audio, and pauses when another source claims.
 */
export function bindTransportMediaExclusivity(
    player: Pick<Player, 'playing$' | 'pause'>
): Unsubscribe {
    const { add, dispose } = createDisposer()
    add(
        player.playing$.onData((playing) => {
            if (playing) claimMediaPlayback(TRANSPORT_MEDIA_SOURCE)
        })
    )
    add(onMediaYield(TRANSPORT_MEDIA_SOURCE, () => player.pause()))
    return dispose
}
